import { create } from 'zustand';
import { useEffect } from 'react';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark';

interface ThemeState {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'light',
      toggleMode: () =>
        set((state) => ({
          mode: state.mode === 'light' ? 'dark' : 'light',
        })),
      setMode: (mode) => set({ mode }),
    }),
    { name: 'jj-theme' }
  )
);

/**
 * Sync Zustand theme mode to DOM (dark class + localStorage).
 * Call once in App root.
 */
export function useThemeSideEffects() {
  const mode = useThemeStore((s) => s.mode);

  useEffect(() => {
    localStorage.setItem('theme-mode', mode);
    const root = document.documentElement;
    if (mode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [mode]);
}

/** Drop-in replacement for the old `useThemeMode()` from theme-context */
export function useThemeMode() {
  const mode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggleMode);
  const setTheme = useThemeStore((s) => s.setMode);
  return { mode, toggleTheme, setTheme };
}
